import { useParams } from "react-router-dom";
import { formatDistance } from "date-fns";
import { useMemo } from "react";
import { BottomSheet } from "react-spring-bottom-sheet";
import { Button, Modal, Spinner } from "react-bootstrap";

import * as Style from "./EntriesScreen.style";
import useCompetition, {
  useCompetitionEntries,
} from "../../../hooks/useCompetition";
import useEntriesScreen from "./useEntriesScreen";
import ScreenHeader from "../../ScreenHeader/ScreenHeader";
import CategorySelector from "../../CategorySelector";
import { EntryOverallScore } from "../../../modules/entry/components/EntryScorecard/EntryScorecard";
import Avatar from "../../Avatar";
import {
  ApiCategory,
  ApiCompetition,
  ApiCompetitionEntryInline,
} from "../../../types";
import { IconButton } from "../../button";
import "react-spring-bottom-sheet/dist/style.css";
import TextInput from "../../TextInput";
import { SettingsSvg } from "../../icons";

type Entry = Omit<ApiCompetitionEntryInline, "category"> & {
  category?: ApiCategory;
};

const EntryTableRow = ({
  entry,
  competition,
  isSelected,
  onClick,
  onActionClick,
  selectedCategories,
  canModify = false,
}: {
  entry: Entry;
  competition: ApiCompetition;
  isSelected?: boolean;
  onClick: () => void;
  onActionClick: () => void;
  selectedCategories: ApiCategory[];
  canModify: boolean;
}) => {
  const ageStr = useMemo(
    () =>
      entry.user.creationTime
        ? formatDistance(new Date(entry.user.creationTime), new Date())
        : "",
    [entry.user]
  );
  return (
    <tr
      key={entry.user.uuid}
      onClick={onClick}
      className={isSelected ? "selected" : ""}
    >
      <td className="position">{entry.position}</td>
      <th>
        {isSelected && canModify ? (
          <IconButton className="actions" onClick={onActionClick}>
            <SettingsSvg />
          </IconButton>
        ) : null}
        <div>
          <div className="avatar">
            <Avatar user={entry.user} size={32} />
          </div>
          <span className="name">
            {entry.user.firstName} {entry.user.lastName}
            {selectedCategories.length !== 1 ? (
              <>
                <br />
                <small>{entry.category?.name}</small>
              </>
            ) : null}
          </span>
          {entry.status === "RETIRED" ? "(R)" : null}
        </div>
        {isSelected && canModify ? (
          <div className="info">
            {!entry.user.parent ? (
              <span className="email">{entry.user.email}</span>
            ) : null}
            {entry.user.parent ? (
              <>
                <em>
                  Parent:{" "}
                  <strong>
                    {entry.user.parent.firstName} {entry.user.parent.lastName}
                  </strong>{" "}
                  ({entry.user.parent.email})
                </em>
              </>
            ) : null}{" "}
            <br />
            {entry.user.creationTime ? <>Account age: {ageStr}</> : null}
            {competition.settings.hasCompetitorNumbers ? (
              <div>
                Competitor number: <strong>{entry.competitorNumber}</strong>
              </div>
            ) : null}
          </div>
        ) : null}
      </th>
      {competition.scoringSystem === "IFSC_SPORT" ? (
        <td className="score score-tuple">{entry.score.toFixed(2)}</td>
      ) : competition.scoringSystem === "IFSC_RANKING" ? (
        <>
          <td className="score score-tuple">{entry.scoreTuple[0]}</td>
          <td className="score score-tuple">{entry.scoreTuple[1]}</td>
          <td className="score score-tuple">{-(entry.scoreTuple[2] ?? 0)}</td>
          <td className="score score-tuple">{-(entry.scoreTuple[3] ?? 0)}</td>
        </>
      ) : (
        <>
          <td className="score score-tuple">{entry.score.toFixed(0)}</td>
        </>
      )}
    </tr>
  );
};

const AccountMergeSummaryInline = ({
  entry,
  competition,
}: {
  entry: Entry;
  competition: ApiCompetition;
}) => {
  let scoreStr;
  if (competition.scoringSystem === "IFSC_SPORT") {
    scoreStr = entry.score.toFixed() + "pts";
  } else {
    scoreStr = `${entry.scoreTuple[0]}T ${entry.scoreTuple[1]}Z, ${-(
      entry.scoreTuple[2] || 0
    )}TA ${-(entry.scoreTuple[3] || 0)}ZA`;
  }
  const ageStr = useMemo(
    () =>
      entry.user.creationTime
        ? formatDistance(new Date(entry.user.creationTime), new Date())
        : "",
    [entry.user]
  );
  return (
    <Style.AccountSummaryInline>
      <strong>
        <Avatar user={entry.user} size={24} />
        <span>
          {entry.user.firstName} {entry.user.lastName}
        </span>
      </strong>
      <br />
      {entry.category?.name} <span className="sep">/</span> {scoreStr}{" "}
      <span className="sep">/</span> {entry.attemptsCount} attempts
      <br />
      <small>
        {entry.user.parent?.email || entry.user.email} <br /> Account age:{" "}
        {ageStr}
      </small>
    </Style.AccountSummaryInline>
  );
};

const EntriesScreen = () => {
  const {
    competition,
    entries,
    selectedCategories,
    setSelectedCategoryUUIDs,
    selectedEntryUUID,
    selectedEntry,
    onEntryClick,
    isBottomSheetOpen,
    setIsBottomSheetOpen,
    onDeleteSelectedEntry,
    isMerging,
    setIsMerging,
    onCancelMerge,
    onConfirmMerge,
    mergeTarget,
    searchText,
    setSearchText,
    isMutateInProgress,
    onModifyEntryClick,
    canViewLeaderboard,
    canAddEntries,
    canModifyEntries,
    isScorecardModalOpen,
    setIsScorecardModalOpen,
  } = useEntriesScreen();

  if (canViewLeaderboard === false) {
    return (
      <Style.Container>
        <ScreenHeader>Leaderboard</ScreenHeader>
        <p>The leaderboard cannot be viewed until the competition ends.</p>
      </Style.Container>
    );
  }

  return (
    <Style.Container>
      <Modal
        show={isScorecardModalOpen}
        onHide={() => setIsScorecardModalOpen(false)}
      >
        <Modal.Header closeButton>Use the Griptonite App</Modal.Header>
        <Modal.Body>
          To see this competitor’s scorecard in detail, open the leaderboard in
          the Griptonite app.
        </Modal.Body>
        <Modal.Footer>
          <Button href="https://go.griptonite.io/app" target="_blank">
            Download the app
          </Button>
        </Modal.Footer>
      </Modal>
      <ScreenHeader>Leaderboard</ScreenHeader>
      {isMerging && selectedEntry ? (
        <Style.Instruction>
          Select another entry to merge into {selectedEntry?.user.firstName}{" "}
          {selectedEntry?.user.lastName}.{" "}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onCancelMerge();
            }}
          >
            Cancel
          </a>
        </Style.Instruction>
      ) : null}
      <Style.ContainerUpper>
        <CategorySelector
          categories={competition?.stages[0]?.categories || []}
          selectedCategories={selectedCategories}
          onChange={(categories) =>
            setSelectedCategoryUUIDs(categories.map((x) => x.uuid))
          }
        />
      </Style.ContainerUpper>
      <TextInput
        type="text"
        value={searchText}
        placeholder="Search competitors..."
        onChange={(e) => setSearchText(e.target.value)}
      />
      {competition && entries ? (
        <Style.EntriesTable>
          <thead>
            <tr>
              <th className="position"></th>
              <th>Name</th>
              {competition.scoringSystem === "IFSC_RANKING" ? (
                <>
                  <th className="score score-tuple">T</th>
                  <th className="score score-tuple">Z</th>
                  <th className="score score-tuple">TA</th>
                  <th className="score score-tuple">ZA</th>
                </>
              ) : (
                <>
                  <th className="score score-tuple">Score</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {entries?.map((entry) => (
              <EntryTableRow
                onActionClick={() => setIsBottomSheetOpen(true)}
                key={entry.user.uuid}
                entry={entry}
                competition={competition}
                isSelected={entry.user.uuid === selectedEntryUUID}
                onClick={() => onEntryClick(entry.user.uuid)}
                selectedCategories={selectedCategories}
                canModify={canModifyEntries}
              />
            ))}
          </tbody>
        </Style.EntriesTable>
      ) : null}
      <BottomSheet
        open={isBottomSheetOpen}
        onDismiss={() => setIsBottomSheetOpen(false)}
      >
        {selectedEntry && !mergeTarget ? (
          <>
            <Style.BottomSheetTitle>
              {selectedEntry.user.firstName} {selectedEntry.user.lastName}
            </Style.BottomSheetTitle>
            {!isMutateInProgress ? (
              <Style.BottomSheetMenu>
                <Style.BottomSheetMenuItem
                  onClick={onDeleteSelectedEntry}
                  className="danger"
                >
                  Delete entry
                </Style.BottomSheetMenuItem>
                <Style.BottomSheetMenuItem
                  onClick={() => {
                    setIsMerging(true);
                    setIsBottomSheetOpen(false);
                  }}
                >
                  Merge with entry...
                </Style.BottomSheetMenuItem>
                <Style.BottomSheetMenuItem
                  onClick={() => onModifyEntryClick(selectedEntry.user.uuid)}
                >
                  Edit competitor details
                </Style.BottomSheetMenuItem>
                <Style.BottomSheetMenuItem
                  onClick={() => setIsBottomSheetOpen(false)}
                  className="cancel"
                >
                  Cancel
                </Style.BottomSheetMenuItem>
              </Style.BottomSheetMenu>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 8,
                }}
              >
                <Spinner animation="border" className="mx-2" /> Deleting...
              </div>
            )}
          </>
        ) : null}
        {selectedEntry && competition && mergeTarget ? (
          <>
            <Style.BottomSheetTitle>Merging entries</Style.BottomSheetTitle>
            <Style.BottomSheetText>
              We will merge:
              <br />
              <AccountMergeSummaryInline
                entry={mergeTarget}
                competition={competition}
              />
              <br />
              into
              <br />
              <AccountMergeSummaryInline
                entry={selectedEntry}
                competition={competition}
              />
            </Style.BottomSheetText>
            {!isMutateInProgress ? (
              <Style.BottomSheetMenu>
                <Style.BottomSheetMenuItem
                  onClick={onConfirmMerge}
                  className="danger"
                >
                  Confirm
                </Style.BottomSheetMenuItem>
                <Style.BottomSheetMenuItem
                  onClick={() => setIsBottomSheetOpen(false)}
                  className="cancel"
                >
                  Cancel
                </Style.BottomSheetMenuItem>
              </Style.BottomSheetMenu>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 8,
                }}
              >
                <Spinner animation="border" className="mx-2" /> Merging...
              </div>
            )}
          </>
        ) : null}
      </BottomSheet>
    </Style.Container>
  );
};

export default EntriesScreen;
